import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api, errorMessage, publicAssetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

/**
 * Admin manager for One-Stop Hub partner brands. Simple table:
 * logo + name + affiliate_id input + active toggle + Save row-level.
 */
export default function AdminPartnerBrandsPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dirty, setDirty] = useState({}); // slug -> patch object

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/partner-brands");
            const items = Array.isArray(res.data) ? res.data : (res.data.items || []);
            items.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            setRows(items);
            setDirty({});
        } catch (e) {
            toast.error(errorMessage(e));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const setField = (slug, field, value) => {
        setDirty((d) => ({ ...d, [slug]: { ...(d[slug] || {}), [field]: value } }));
    };

    const save = async (row) => {
        const patch = dirty[row.slug];
        if (!patch || !Object.keys(patch).length) {
            toast("Nothing to save");
            return;
        }
        try {
            await api.patch(`/admin/partner-brands/${row.id}`, patch);
            toast.success(`Saved ${row.name}`);
            load();
        } catch (e) { toast.error(errorMessage(e)); }
    };

    const toggleActive = async (row, next) => {
        try {
            await api.patch(`/admin/partner-brands/${row.id}`, { is_active: next });
            toast.success(`${row.name} ${next ? "activated" : "deactivated"}`);
            load();
        } catch (e) { toast.error(errorMessage(e)); }
    };

    return (
        <div className="space-y-6" data-testid="admin-partner-brands-page">
            <header>
                <div className="font-heading text-sm font-semibold text-[var(--brand-orange)]">Growth</div>
                <h1 className="mt-1 font-heading text-3xl font-extrabold text-[var(--text-primary)] sm:text-4xl">Partner Brands</h1>
                <p className="mt-1 max-w-2xl font-body text-sm text-[var(--text-muted)]">
                    One-Stop Hub aggregators. Paste your affiliate ID for each partner — the customer app will inject it into every outbound link.
                </p>
            </header>

            <div className="card-soft flex items-center justify-between">
                <div className="font-body text-sm text-[var(--text-muted)]">{rows.length} brand{rows.length === 1 ? "" : "s"}</div>
                <Button variant="outline" onClick={load} disabled={loading} className="rounded-xl border-slate-200">
                    <RefreshCw size={14} className={"mr-2 " + (loading ? "animate-spin" : "")} /> Refresh
                </Button>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left font-heading text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                        <tr>
                            <th className="px-4 py-3">Brand</th>
                            <th className="px-4 py-3">Category</th>
                            <th className="px-4 py-3">Affiliate ID</th>
                            <th className="px-4 py-3">Active</th>
                            <th className="px-4 py-3 text-right">&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-slate-100">
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl ring-1 ring-slate-100"
                                            style={{ background: (r.brand_color || "#FFF3E6") + "18" }}
                                        >
                                            {r.logo_url ? (
                                                <img src={publicAssetUrl(r.logo_url)} alt={r.name} className="h-7 w-7 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                                            ) : (
                                                <span className="font-heading text-sm font-bold" style={{ color: r.brand_color || "var(--brand-orange)" }}>{r.name.charAt(0)}</span>
                                            )}
                                        </div>
                                        <div className="font-heading text-sm font-semibold text-[var(--text-primary)]">{r.name}</div>
                                    </div>
                                </td>
                                <td className="px-4 py-3 font-body text-xs text-[var(--text-muted)] capitalize">{r.category || "—"}</td>
                                <td className="px-4 py-3">
                                    <Input
                                        defaultValue={r.affiliate_id || ""}
                                        onChange={(e) => setField(r.slug, "affiliate_id", e.target.value)}
                                        placeholder="tag-20 · offernest-pub · …"
                                        data-testid={`admin-partner-brand-affiliate-input-${r.slug}`}
                                        className="max-w-xs"
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <Switch
                                        checked={r.is_active !== false}
                                        onCheckedChange={(v) => toggleActive(r, v)}
                                        className="data-[state=checked]:bg-[var(--brand-orange)]"
                                        data-testid={`admin-partner-brand-active-${r.slug}`}
                                    />
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <Button
                                        onClick={() => save(r)}
                                        disabled={!dirty[r.slug]}
                                        className="btn-primary rounded-full"
                                        data-testid={`admin-partner-brand-save-${r.slug}`}
                                    >
                                        Save
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
